<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  mapping: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const dealValue = ref(props.mapping.dealValue || null);
const dealCurrency = ref(props.mapping.dealCurrency || 'BRL');
const probability = ref(props.mapping.probability || 50);
const expectedCloseDate = ref(props.mapping.expectedCloseDate || null);

const currencies = [
  { value: 'BRL', label: 'R$ (BRL)' },
  { value: 'USD', label: '$ (USD)' },
  { value: 'EUR', label: '€ (EUR)' },
];

const hasChanges = computed(() => {
  return (
    dealValue.value !== props.mapping.dealValue ||
    dealCurrency.value !== props.mapping.dealCurrency ||
    probability.value !== props.mapping.probability ||
    expectedCloseDate.value !== props.mapping.expectedCloseDate
  );
});

const weightedValue = computed(() => {
  if (!dealValue.value || !probability.value) return 0;
  return ((dealValue.value * probability.value) / 100).toFixed(2);
});

const currencySymbols = {
  USD: '$',
  EUR: '€',
  BRL: 'R$',
};

const formattedWeightedValue = computed(() => {
  const symbol = currencySymbols[dealCurrency.value] || 'R$';
  return `${symbol} ${weightedValue.value}`;
});

watch(
  () => props.mapping,
  newMapping => {
    dealValue.value = newMapping.dealValue || null;
    dealCurrency.value = newMapping.dealCurrency || 'BRL';
    probability.value = newMapping.probability || 50;
    expectedCloseDate.value = newMapping.expectedCloseDate || null;
  }
);

function handleSave() {
  emit('save', {
    deal_value: dealValue.value,
    deal_currency: dealCurrency.value,
    probability: probability.value,
    expected_close_date: expectedCloseDate.value,
  });
}

function handleCancel() {
  dealValue.value = props.mapping.dealValue || null;
  dealCurrency.value = props.mapping.dealCurrency || 'BRL';
  probability.value = props.mapping.probability || 50;
  expectedCloseDate.value = props.mapping.expectedCloseDate || null;
  emit('cancel');
}
</script>

<template>
  <div class="deal-editor">
    <div class="deal-editor__header">
      <h3 class="deal-editor__title">
        {{ t('CRM.DEAL.EDITOR_TITLE') }}
      </h3>
    </div>

    <div class="deal-editor__form">
      <!-- Deal Value -->
      <div class="deal-editor__field">
        <label class="deal-editor__label">
          {{ t('CRM.DEAL.VALUE') }}
        </label>
        <div class="deal-editor__value-row">
          <select v-model="dealCurrency" class="deal-editor__currency-select">
            <option
              v-for="currency in currencies"
              :key="currency.value"
              :value="currency.value"
            >
              {{ currency.label }}
            </option>
          </select>
          <input
            v-model.number="dealValue"
            type="number"
            step="0.01"
            min="0"
            class="deal-editor__value-input"
            :placeholder="t('CRM.DEAL.VALUE_PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Probability -->
      <div class="deal-editor__field">
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <label class="deal-editor__label">
          {{ t('CRM.DEAL.PROBABILITY') }} ({{ probability }}%)
        </label>
        <!-- eslint-enable vue/no-bare-strings-in-template -->
        <input
          v-model.number="probability"
          type="range"
          min="0"
          max="100"
          step="5"
          class="deal-editor__probability-slider"
        />
        <div class="deal-editor__probability-labels">
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <span>0%</span>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <span>50%</span>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <span>100%</span>
        </div>
      </div>

      <!-- Weighted Value -->
      <div v-if="dealValue && probability" class="deal-editor__field">
        <label class="deal-editor__label">
          {{ t('CRM.DEAL.WEIGHTED_VALUE') }}
        </label>
        <div class="deal-editor__weighted-value">
          {{ formattedWeightedValue }}
        </div>
      </div>

      <!-- Expected Close Date -->
      <div class="deal-editor__field">
        <label class="deal-editor__label">
          {{ t('CRM.DEAL.EXPECTED_CLOSE_DATE') }}
        </label>
        <input
          v-model="expectedCloseDate"
          type="date"
          class="deal-editor__date-input"
        />
      </div>
    </div>

    <div class="deal-editor__actions">
      <button
        class="deal-editor__btn deal-editor__btn--cancel"
        :disabled="isLoading"
        @click="handleCancel"
      >
        {{ t('CRM.DEAL.CANCEL') }}
      </button>
      <button
        class="deal-editor__btn deal-editor__btn--save"
        :disabled="!hasChanges || isLoading"
        @click="handleSave"
      >
        <span v-if="isLoading">{{ t('CRM.DEAL.SAVING') }}</span>
        <span v-else>{{ t('CRM.DEAL.SAVE') }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.deal-editor {
  @apply p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm;
}

.deal-editor__header {
  @apply mb-4 pb-2 border-b border-slate-200 dark:border-slate-700;
}

.deal-editor__title {
  @apply text-base font-semibold text-slate-800 dark:text-slate-100;
}

.deal-editor__form {
  @apply space-y-4;
}

.deal-editor__field {
  @apply flex flex-col gap-1;
}

.deal-editor__label {
  @apply text-sm font-medium text-slate-600 dark:text-slate-400;
}

.deal-editor__value-row {
  @apply flex gap-2;
}

.deal-editor__currency-select {
  @apply w-24 px-2 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200;
}

.deal-editor__value-input {
  @apply flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200;
}

.deal-editor__probability-slider {
  @apply w-full h-2 rounded-lg appearance-none cursor-pointer bg-slate-200 dark:bg-slate-600;
}

.deal-editor__probability-labels {
  @apply flex justify-between text-xs text-slate-500 dark:text-slate-400;
}

.deal-editor__weighted-value {
  @apply text-lg font-semibold text-green-600 dark:text-green-400;
}

.deal-editor__date-input {
  @apply px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200;
}

.deal-editor__actions {
  @apply flex justify-end gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700;
}

.deal-editor__btn {
  @apply px-4 py-2 text-sm font-medium rounded-md transition-colors;
}

.deal-editor__btn--cancel {
  @apply text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700;
}

.deal-editor__btn--save {
  @apply bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>
